import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import {
  AddIcon,
  Button,
  FlexContent,
  Form,
  FormField,
  Input,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminLoadDurationClass } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

interface FormBody {
  code: string;
  name: string;
}

const AdminLoadDurationClassesTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "loadDurationClasses" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useLoadDurationClasses(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminLoadDurationClass>();
  const [itemToEdit, setItemToEdit] = useState<AdminLoadDurationClass>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminLoadDurationClass>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.code}</TableCell>
              <TableCell>{item.name}</TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.loadDurationClasses.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteLoadDurationClass(itemToDelete.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

const FormModal: FC<{ item?: AdminLoadDurationClass; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();

  const defaultValues: FormBody = {
    code: item?.code ?? "",
    name: item?.name ?? "",
  };

  const onSubmit = () => async (formData: FormBody) => {
    try {
      if (item) {
        await adminApi.updateLoadDurationClass(item.id, formData);
      } else {
        await adminApi.insertLoadDurationClass(formData);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        item
          ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.loadDurationClasses.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.loadDurationClasses.singular}`
      }
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="code"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.code} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.name} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminLoadDurationClassesTab;
